import { useCallback, useContext, useEffect } from 'react';
import ReactModal from 'react-modal';
import { CurrentCompanyContext } from '../hooks/CurrentCompanyContext';
import { loadRewardfulScript } from '../utils/rewardful';
import Modal, { MODAL_STYLES } from './Modal';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    planName?: string;
}

export default function UpgradePlanModal({ isOpen, onClose }: Props) {
    const company = useContext(CurrentCompanyContext);
    const handleRender = useCallback(() => {
        if (!process.env.REACT_APP_CUSTOMER_PORTAL_ID || !company) {
            return;
        }

        if (typeof window.pricewellSnippet?.init === 'function') {
            window.pricewellSnippet?.init?.();
        } else {
            const script = document.createElement('script');
            script.src = `${process.env.REACT_APP_SNIPPET_SERVER_URL}/${process.env.REACT_APP_PRICING_PAGE_ID}/pricewell.js`;
            document.head.appendChild(script);
        }
    }, [company]);

    useEffect(loadRewardfulScript, []);
    return (
        <ReactModal isOpen={isOpen} style={MODAL_STYLES}>
            <Modal
                type="INFO"
                title="Your Account Requires an Upgrade"
                centered
                body={
                    <div className="text-center">
                        <div className="flex justify-center pb-6">
                            <div
                                id={`pricewell-${process.env.REACT_APP_PRICING_PAGE_ID}`}
                                data-stripe-id={company?.stripeCustomerId}
                                data-disable-free-plans="true"
                                ref={handleRender}
                            ></div>
                        </div>
                        <div className="flex justify-center pb-6">30 day money back guarantee</div>
                        <img src="/images/testimonial-avatars.png" alt="testimonial avatars" className="m-auto" />

                        <section className="text-gray-600 body-font">
                            <div className="container px-5 py-6 mx-auto">
                                <div className="flex flex-col text-center w-full mb-20">
                                    <h1 className="sm:text-3xl text-2xl font-medium title-font text-gray-900">
                                        Advanced features, no need to hire a developer
                                    </h1>
                                </div>
                                <div className="flex flex-wrap -m-4">
                                    <div className="p-4 md:w-1/3">
                                        <div className="flex rounded-lg h-full bg-gray-100 p-8 flex-col">
                                            <div className="flex items-center mb-3">
                                                <div className="w-8 h-8 mr-3 inline-flex items-center justify-center rounded-full bg-white text-white shrink-0">
                                                    <img
                                                        src="/images/integrations/bubble-io.png"
                                                        className="w-5"
                                                        alt=""
                                                    />
                                                </div>
                                                <h2 className="text-gray-900 text-lg title-font font-medium">
                                                    Bubble Integration
                                                </h2>
                                            </div>
                                            <div className="flex-grow">
                                                <p className="leading-relaxed text-base">
                                                    Automatically sync your date from Stripe to Bubble.io. Save hours of
                                                    setup and have access to advanced logging and analytics.
                                                </p>
                                                <a
                                                    href="https://www.pricewell.com/bubble/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="mt-3 text-indigo-500 inline-flex items-center"
                                                >
                                                    Learn More
                                                    <svg
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        className="w-4 h-4 ml-2"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path d="M5 12h14M12 5l7 7-7 7"></path>
                                                    </svg>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4 md:w-1/3">
                                        <div className="flex rounded-lg h-full bg-gray-100 p-8 flex-col">
                                            <div className="flex items-center mb-3">
                                                <div className="w-8 h-8 mr-3 inline-flex items-center justify-center rounded-full bg-blue-500 text-white shrink-0">
                                                    <svg
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        className="w-5 h-5"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"></path>
                                                        <circle cx="12" cy="7" r="4"></circle>
                                                    </svg>
                                                </div>
                                                <h2 className="text-gray-900 text-lg title-font font-medium">
                                                    Customer Portal
                                                </h2>
                                            </div>
                                            <div className="flex-grow">
                                                <p className="leading-relaxed text-base">
                                                    Save money, time and prevent churn by letting your customers manage
                                                    their subscriptions. Upgrade plans, update card details, download
                                                    invoices, collect cancel reasons and more.
                                                </p>
                                                <a
                                                    href="https://www.pricewell.com/customer-portal/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="mt-3 text-indigo-500 inline-flex items-center"
                                                >
                                                    Learn More
                                                    <svg
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        className="w-4 h-4 ml-2"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path d="M5 12h14M12 5l7 7-7 7"></path>
                                                    </svg>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4 md:w-1/3">
                                        <div className="flex rounded-lg h-full bg-gray-100 p-8 flex-col">
                                            <div className="flex items-center mb-3">
                                                <div className="w-8 h-8 mr-3 inline-flex items-center justify-center rounded-full bg-blue-500 text-white shrink-0">
                                                    <svg
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        className="w-5 h-5"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle cx="6" cy="6" r="3"></circle>
                                                        <circle cx="6" cy="18" r="3"></circle>
                                                        <path d="M20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12"></path>
                                                    </svg>
                                                </div>
                                                <h2 className="text-gray-900 text-lg title-font font-medium">
                                                    Gated Content
                                                </h2>
                                            </div>
                                            <div className="flex-grow">
                                                <p className="leading-relaxed text-base">
                                                    Hide content for customers who aren't on specific plans. No backend
                                                    is required, drop the snippet into your site. Great for newsletters,
                                                    courses, communities and podcasts.
                                                </p>
                                                <a
                                                    href="https://www.pricewell.com/gated-content/"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="mt-3 text-indigo-500 inline-flex items-center"
                                                >
                                                    Learn More
                                                    <svg
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        className="w-4 h-4 ml-2"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path d="M5 12h14M12 5l7 7-7 7"></path>
                                                    </svg>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                }
                confirmButtonText="Cancel"
                confirmButtonStyle="text"
                onConfirm={onClose}
            />
        </ReactModal>
    );
}
